// Utility functions to prepare data for Sankey diagram

/**
 * Transforms budget data into the format required by the Sankey diagram
 * @param {string} selectedMonth - The currently selected month (e.g., "Jan-25")
 * @param {object} budgetData - The budget data object from your application
 * @param {array} accounts - Array of account objects
 * @param {array} categories - Array of category objects
 * @returns {object} Formatted data for the Sankey diagram with nodes and links
 */
const prepareSankeyData = (selectedMonth, budgetData, accounts, categories = []) => {
    if (!selectedMonth) return null;

    // If we're looking at actual data (not forecast)
    const monthData = budgetData.monthlySummary[selectedMonth];
    if (!monthData) return null;

    // Budget data is keyed by category ID (e.g. 'app_development');
    // resolve to the category's display name for chart labels
    const categoryNameMap = {};
    categories.forEach(c => {
      if (c.id) categoryNameMap[c.id] = c.name || c.id;
    });
    const getDisplayName = (categoryId) =>
      categoryId === 'uncategorized' ? 'Uncategorized' : (categoryNameMap[categoryId] || categoryId);

    // Filter and map income categories with non-zero amounts
    const incomeData = Object.entries(budgetData.incomeCategories)
      .filter(([_, amounts]) => amounts[selectedMonth] && amounts[selectedMonth] !== 0)
      .map(([category, amounts]) => ({
        name: getDisplayName(category),
        amount: amounts[selectedMonth] || 0
      }))
      // Sort by amount in descending order
      .sort((a, b) => b.amount - a.amount);

    // Filter and map expense categories with non-zero amounts
    const rawExpenseData = Object.entries(budgetData.expenseCategories)
      .filter(([_, amounts]) => amounts[selectedMonth] && amounts[selectedMonth] !== 0)
      .map(([category, amounts]) => ({
        name: getDisplayName(category),
        amount: amounts[selectedMonth] || 0
      }))
      // Sort by amount in descending order
      .sort((a, b) => b.amount - a.amount);

    // Pass all categories through — the # Categories slider handles grouping
    const positiveExpenseData = rawExpenseData.filter(d => d.amount > 0);
    const negativeTotal = rawExpenseData
      .filter(d => d.amount < 0)
      .reduce((sum, d) => sum + d.amount, 0);

    const totalExpenses = monthData.expenses;
    const expenseData = [
      ...positiveExpenseData,
      ...(negativeTotal < 0 ? [{ name: 'Other', amount: negativeTotal }] : [])
    ];
    
    const totalIncome = monthData.income;
    const savings = totalIncome - totalExpenses;
    
    // Nodes for the Sankey diagram
    const nodes = [
      // Income sources
      ...incomeData.map((cat, i) => ({
        id: i,
        name: cat.name,
        category: 'income',
        value: cat.amount
      })),
      
      // Central node
      {
        id: incomeData.length,
        name: 'Total Income',
        category: 'general',
        value: incomeData.reduce((sum, cat) => sum + cat.amount, 0)
      },
      
      // Expense categories
      ...expenseData.map((cat, i) => ({
        id: incomeData.length + 1 + i,
        name: cat.name,
        category: 'expenses',
        value: cat.amount
      })),
      
      // Net Balance node (if positive)
      ...(savings > 0 ? [{
        id: incomeData.length + expenseData.length + 1,
        name: 'Net Balance',
        category: 'savings',
        value: savings
      }] : [])
    ];
    
    
    // Links between nodes
    const links = [
      // Links from income sources to total income
      ...incomeData.map((cat, i) => ({
        source: i,
        target: incomeData.length, // The central 'Total Income' node
        value: cat.amount
      })),
      
      // Links from total income to expense categories
      ...expenseData.map((cat, i) => ({
        source: incomeData.length,
        target: incomeData.length + 1 + i,
        value: cat.amount
      })),
      
      // Link from total income to savings (if positive)
      ...(savings > 0 ? [{
        source: incomeData.length,
        target: incomeData.length + expenseData.length + 1,
        value: savings
      }] : [])
    ];
    
    
    return { nodes, links };
};

/**
 * Prepares data for the income vs expenses line chart
 * @param {object} budgetData - The budget data object from your application
 * @param {number} monthsToShow - Number of months to include in the chart (default: 6)
 * @returns {array} Array of data points for the chart
 */
const prepareChartData = (budgetData, monthsToShow = 6) => {
    if (!budgetData || !budgetData.months || budgetData.months.length === 0) {
      return [];
    }
    
    // Get the most recent N months
    const numMonths = Math.min(monthsToShow, budgetData.months.length);
    const relevantMonths = budgetData.months.slice(-numMonths);
    
    return relevantMonths.map(month => {
      const monthData = { month };
      
      // Add category data
      Object.entries(budgetData.incomeCategories).forEach(([category, amounts]) => {
        if (amounts[month] > 0) {
          monthData[category] = amounts[month];
        }
      });
      
      Object.entries(budgetData.expenseCategories).forEach(([category, amounts]) => {
        if (amounts[month] > 0) {
          monthData[category] = amounts[month];
        }
      });
      
      return monthData;
    });
};

export { prepareSankeyData, prepareChartData };